/**
 * Formats a number with locale-aware formatting.
 * Supports both currency and normal number formatting.
 *
 * @param number - The number to format (default: 0)
 * @param type - The format type: 'normal' for regular numbers, 'currency' for currency (default: 'normal')
 * @param dp - Decimal places (default: 0)
 * @param currency - Currency code for currency formatting, e.g., 'GBP', 'USD' (default: 'GBP')
 * @returns Formatted number string, or null if input is NaN
 */
export function formatNumber(
  number: number = 0,
  type: 'normal' | 'currency' = 'normal',
  dp: number = 0,
  currency: string = 'GBP'
): string | null {
  const locale = localStorage.getItem('locale') || 'en-GB'
  let options: Intl.NumberFormatOptions = {}

  if (isNaN(number)) {
    return null
  }

  // If Currency
  if (type === 'currency') {
    options = {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: dp,
      maximumFractionDigits: dp
    }
  } else {
    options = {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp
    }
  }

  const formatter = new Intl.NumberFormat(locale, options)
  return formatter.format(number)
}

/**
 * Formats a number as currency with locale-aware formatting.
 * Convenience function that calls formatNumber with type='currency'.
 *
 * @param number - The number to format
 * @param currency - Currency code, e.g., 'GBP', 'USD' (default: 'GBP')
 * @param dp - Decimal places (default: 0)
 * @returns Formatted currency string, or null if input is NaN
 */
export function formatCurrency(
  number: number,
  currency: string = 'GBP',
  dp: number = 0
): string | null {
  return formatNumber(number, 'currency', dp, currency)
}

